import { styled } from "@material-ui/core";
import {
  attributes, AttributesMixin,
  define,
  Store,
  subsetOf
} from "@type-r/models";
import { useIO, useModel } from "@type-r/react";
import { asCurrency, asPercent, CompactInput, CompactSelectDateRange, CompactTabs, div, Flex, Loading, Page, ProgressCircle, SectionTitle, SelectMultiEx, Tab } from 'lqx-react';
import * as moment from "moment";
import * as React from "react";
import { globalStore } from '../../../../../api/store';
import { Account } from "../api/account";
import { CashLot } from '../api/cash-lot';
import { Currency } from "../api/currency";
import { Region } from "../api/region";
import { CashBalance } from "./cash-balance";
import { DaysDeliquent, DSO } from "./collection-days";
import { ForecastToActual } from "./forecast-to-actual";
import { WorkingCapital } from "./working-capital";



interface State extends AttributesMixin<typeof State> { }

const DefaultSettings = attributes({
  minTime: Date,
  maxTime: Date,
  effectiveDate: Date,
  selectedRegions: subsetOf("owner.regions"),
  selectedCurrencies: subsetOf("owner.currencies"),
  selectedCompanies: subsetOf("owner.companies"),
  selectedAccounts: subsetOf("owner.accounts")
});

@define
class State extends Store {
  static attributes = {
    settings: DefaultSettings,
    accountReceivableValue: 0,
    accountReceivablePercent: 0,
    cashAppliedValue: 0,
    cashAppliedPercent: 0,
    dsoAverage: 0,
    averageDaysDeliquent: 0,
    companies: [{
      id: "",
      label: "",
      conversationIds: Array
    }],
    accounts: [Account],
    regions: [Region],
    currencies: [Currency],
    cashLot: CashLot,
    frequency: "day"
  };
}

const getUniqueValues = (entryValues: any[]): any[] => {
  const set = new Set(entryValues);
  return Array.from(set.values());
};

const getInterval = (frequency: string): [number, string] => {
  switch (frequency) {
    case "day": return [1, "week"];
    case "week": return [4, "week"];
    case "fortnight": return [8, "week"];
    case "month": return [3, "M"];
    case "quarter":
    case "year": return [1, "y"];
  }
};

export const CashMonitorPage = ({ }: {}) => {
  const state = useModel(State);

  React.useEffect(() => {
    const currentDate = new Date(), y = currentDate.getFullYear(), m = currentDate.getMonth();
    const firstDayOfCurrentMonth = new Date(y, m, 1);
    const lastDayOfCurrentMonth = new Date(y, m + 1, 0);
    state.settings.minTime = firstDayOfCurrentMonth;
    state.settings.maxTime = lastDayOfCurrentMonth;
  }, []);

  React.useEffect(() => {
    if (state.settings.minTime > state.settings.maxTime) {
      const [amount, unit] = getInterval(state.frequency);
      state.settings.maxTime = moment(state.settings.minTime).add(amount, unit as any).toDate();
    }
  }, [state.settings.minTime]);

  React.useEffect(() => {
    const companies = new Map<number, { label: string, relationshipIds: Set<number>, conversationIds: Set<number> }>();

    for (let relationship of globalStore.relationships.models) {
      for (let participant of relationship.participants.models) {
        if (!companies.has(participant.companyId)) {
          companies.set(participant.companyId, { label: participant.filterSelectionName, relationshipIds: new Set<number>(), conversationIds: new Set<number>() });
        }

        const company = companies.get(participant.companyId);
        company.relationshipIds.add(Number(relationship.id));
      }
    }

    var companyArray = Array.from(companies.entries());
    for (let conversation of globalStore.conversations.models) {
      companyArray.map(([id, company]) => {
        if (company.relationshipIds.has(conversation.relationshipId)) {
          company.conversationIds.add(Number(conversation.id));
        }
      })
    }

    state.companies = companyArray
      .map(([id, company]) => ({
        id,
        label: company.label,
        conversationIds: Array.from(company.conversationIds.values())
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) as any;
  }, [globalStore.relationships['_changeToken']]);

  const refreshTime = (input: Date): Date => {
    var result = new Date();
    result.setFullYear(input.getFullYear());
    result.setMonth(input.getMonth());
    result.setDate(input.getDate());
    return result;
  }

  const isReady = useIO(async () => {
    state.cashLot.startDate = refreshTime(state.settings.minTime);
    state.cashLot.endDate = refreshTime(state.settings.maxTime);
    state.cashLot.effectiveDate = refreshTime(state.settings.effectiveDate);
    state.cashLot.accounts = state.settings.selectedAccounts.map(x => x.id) as any;
    state.cashLot.regions = state.settings.selectedRegions.map(x => x.id) as any;
    state.cashLot.currencies = state.settings.selectedCurrencies.map(x => x.id) as any;
    state.cashLot.conversationIds = getUniqueValues(state.settings.selectedCompanies.reduce((acc, curr) => [...acc, ...curr['conversationIds']], [])) as any;
    state.cashLot.frequency = state.frequency;
    return await state.cashLot.save();
  }, [
    state.settings['_changeToken']
  ]);

  useIO(() =>
    Promise.all([
      state.accounts.fetch().catch(e => console.error(e)),
      state.currencies.fetch().catch(e => console.error(e)),
      state.regions.fetch().catch(e => console.error(e))
    ]));

  React.useEffect(() => {
    const computedValues = state.cashLot.computedValues;
    state.accountReceivableValue = computedValues.unreconciledArInvoicesTotal;
    const accountReceivableTotal = computedValues.unreconciledArInvoicesTotal + computedValues.reconciledArInvoicesTotal;
    state.accountReceivablePercent = accountReceivableTotal === 0 ? 0 : computedValues.reconciledArInvoicesTotal / accountReceivableTotal;

    const cashAppliedTotal = computedValues.bookedArPaymentsTotal + computedValues.unreconciledArPaymentsTotal;
    state.cashAppliedValue = computedValues.bookedArPaymentsTotal;
    state.cashAppliedPercent = cashAppliedTotal === 0 ? 0 : computedValues.bookedArPaymentsTotal / cashAppliedTotal;

    state.dsoAverage = computedValues.averageDso;
    state.averageDaysDeliquent = computedValues.averageDaysDeliquent;
  }, [state.cashLot.items['_changeToken']]);

  return (
    <Page maxWidth={false}>
      <Flex row>
        <CompactInput label="Effective Date" date $value={state.settings.$.effectiveDate} />
        <CompactSelectDateRange fromLabel="From" $from={state.settings.$.minTime} toLabel="To" $to={state.settings.$.maxTime} />
        <Flex grow />
        <CompactTabs $value={state.$.frequency}>
          <Tab value="day">Daily</Tab>
          <Tab value="week">7-day</Tab>
          <Tab value="fortnight">14-day</Tab>
          <Tab value="month">Monthly</Tab>
          <Tab value="quarter">Quarterly</Tab>
          <Tab value="year">Yearly</Tab>
        </CompactTabs>
      </Flex>
      <Flex row>
        <SelectMultiEx placeholder={`${state.settings.selectedCompanies.length > 0 ? 'Selected' : 'All'} Legal Entities`} options={state.companies} selected={state.settings.selectedCompanies} autocomplete />
        <SelectMultiEx placeholder={`${state.settings.selectedRegions.length > 0 ? 'Selected' : 'All'} Regions`} options={state.regions} selected={state.settings.selectedRegions} autocomplete />
        <SelectMultiEx placeholder={`${state.settings.selectedAccounts.length > 0 ? 'Selected' : 'All'} Accounts`} options={state.accounts} selected={state.settings.selectedAccounts} autocomplete />
        <SelectMultiEx placeholder={`${state.settings.selectedCurrencies.length > 0 ? 'Selected' : 'All'} Currencies`} options={state.currencies} selected={state.settings.selectedCurrencies} autocomplete />
        <Flex grow />
      </Flex>

      <WidgetRow>
        <PercentMeterWidget title="Account Receivable" footer="Received" percent={state.accountReceivablePercent} tolerance={0.75} value={state.accountReceivableValue} />
        <PercentMeterWidget title="Total Cash Applied" footer="Applied" percent={state.cashAppliedPercent} tolerance={0.9} value={state.cashAppliedValue} />
        <Widget title={`DSO - ${state.dsoAverage.toFixed(2)}`}>
          {isReady ? <DSO cashLot={state.cashLot} frequency={state.frequency} /> : <Loading />}
        </Widget>
        <Widget title={`Average Days Delinquent - ${state.averageDaysDeliquent.toFixed(2)}`}>
          {isReady ? <DaysDeliquent cashLot={state.cashLot} frequency={state.frequency} /> : <Loading />}
        </Widget>
      </WidgetRow>

      <WidgetRow>
        <Widget title={`Cash Balance by ${state.frequency}`}>
          {isReady ? <CashBalance cashLot={state.cashLot} frequency={state.frequency} /> : <Loading />}
        </Widget>
        <Widget title="AP/AR Working Capital">
          {isReady ? <WorkingCapital cashLot={state.cashLot} frequency={state.frequency} /> : <Loading />}
        </Widget>
      </WidgetRow>

      <WidgetRow>
        <Widget title="Forecast To Actual">
          {isReady ? <ForecastToActual cashLot={state.cashLot} frequency={state.frequency} /> : <Loading />}
        </Widget>
      </WidgetRow>
    </Page>
  );
};

export const WidgetRow = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  height: ({ height }: any) => height
});

const WidgetContainer = div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  margin: 4,
  color: 'rgba(0, 0, 0, 0.87)',
  padding: 4,
  transition: 'box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  backgroundColor: '#fff',
  boxShadow: '2px 0px 3px 0px rgb(0 0 0 / 4%)',
});

const Widget = ({ title, rightTitle, children, column }: {
  children: React.ReactNode,
  title?: React.ReactNode,
  rightTitle?: React.ReactNode,
  column?: boolean
}) =>
  <WidgetContainer>
    <Flex row>
      <SectionTitle>{title}</SectionTitle>
      <Flex grow />
      {rightTitle}
    </Flex>
    <Flex center column={column}>
      {children}
    </Flex>
  </WidgetContainer>;

const PercentageMeterValue = div({
  display: 'flex',
  width: '100%',
  fontWeight: 'bold'
});

const PercentMeterWidget = ({ title, footer, percent, tolerance, value }: {
  title: React.ReactNode,
  footer: React.ReactNode,
  percent: number,
  tolerance: number,
  value: number
}) =>
  <Widget title={title} column>
    <PercentageMeterValue>{asCurrency(value)}</PercentageMeterValue>
    <ProgressCircle
      percentage={percent * 100}
      label={footer.toLocaleString()}
      color={percent >= tolerance ? 'green' : 'yellow'}
    >
      <span> {percent * 100 < 1 && percent !== 0 ? '<1%' : asPercent(percent)} </span>
    </ProgressCircle>
  </Widget>;
